import React, { useState, useEffect } from "react";
import { Search, ChevronDown, Menu, ShieldCheck } from "lucide-react";
import { Button } from "./ui/Button";
import { Input } from "./ui/input";
import { useAuth } from "../auth/AuthContext"; // adjust path
import { useNavigate, useLocation } from "react-router-dom";
import NotificationSidebar from "./NotificationSidebar";
import SearchOverlay from "./SearchOverlay";
import QuickActionDropdown from "./QuickActionDropdown";
import AppointmentSidebar from "./AppointmentSidebar";
import QuickPaymentPanel from "./QuickPaymentPanel";
import PosPaymentStep from "./PosPaymentStep";
import VerifyAccountModal from "./VerifyAccountModal";
import { useEmployees } from "../hooks/useEmployees";
import { getSettings } from "../services/settings";
import { getNotifications, getUnreadCount } from "../services/notification";
import { Portal } from "./ui/Portal";

interface NavbarProps {
  onMobileMenuClick?: () => void;
}

const Navbar = ({ onMobileMenuClick }: NavbarProps) => {
  const [searchValue, setSearchValue] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);
  const [notificationSidebarOpen, setNotificationSidebarOpen] = useState(false);
  const [searchOverlayOpen, setSearchOverlayOpen] = useState(false);
  const [notificationCount, setNotificationCount] = useState(0);

  const { user, logout, syncSettings } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const isClaimed = user?.company?.is_claim === "yes";
  const isSuperAdmin = (user?.roles || []).some(
    (role: any) => role?.name === "superadmin" || role?.name === "agent"
  );
  const [verifyModalOpen, setVerifyModalOpen] = useState(false);

  // Quick Action States
  const [isAppointmentSidebarOpen, setIsAppointmentSidebarOpen] = useState(false);
  const [appointmentSidebarMode, setAppointmentSidebarMode] = useState<"Services" | "Sunbeds">("Services");
  const [showQuickPaymentPanel, setShowQuickPaymentPanel] = useState(false);
  const [sidebarMode, setSidebarMode] = useState<"quick" | "paynow" | null>(null);
  const [quickPaymentType, setQuickPaymentType] = useState<"direct" | "stylist">("stylist");
  const { staffList } = useEmployees();

  const handleQuickAction = (action: "Services" | "Sunbeds" | "Payment") => {
    if (action === "Services") {
      setAppointmentSidebarMode("Services");
      setIsAppointmentSidebarOpen(true);
    } else if (action === "Sunbeds") {
      setAppointmentSidebarMode("Sunbeds");
      setIsAppointmentSidebarOpen(true);
    } else if (action === "Payment") {
      setShowQuickPaymentPanel(true);
    }
  };

  const fetchNotificationCount = async () => {
    if (isSuperAdmin) return;

    try {
      const response = await getUnreadCount();
      if (response.success) {
        setNotificationCount(response.data.unread_count);
      }
    } catch (error) {
      console.error("Failed to fetch notification count:", error);
    }
  };

  useEffect(() => {
    const fetchQuickPaymentType = async () => {
      if (isSuperAdmin) return;

      try {
        const response = await getSettings();
        if (response.success && response.data?.settings) {
          const paymentType = response.data.settings.quick_payment_type;
          if (paymentType === "stylist" || paymentType === "active") {
            setQuickPaymentType("stylist");
          } else if (paymentType === "direct" || paymentType === "inactive") {
            setQuickPaymentType("direct");
          }
        }
      } catch (error) {
        console.error("Failed to fetch quick payment type in navbar:", error);
      }

    };

    fetchQuickPaymentType();
    fetchNotificationCount();

    window.addEventListener("settingsUpdated", fetchQuickPaymentType);
    window.addEventListener("notificationsUpdated", fetchNotificationCount);
    return () => {
      window.removeEventListener("settingsUpdated", fetchQuickPaymentType);
      window.removeEventListener("notificationsUpdated", fetchNotificationCount);
    };
  }, [location.pathname, isSuperAdmin]);

  const currencySymbol = user?.company?.currency_symbol || user?.company?.currency?.currency_symbol || "£";

  return (
    <nav className="px-4 lg:px-6 py-3 shadow-lg sticky top-0 z-[100]" style={{ backgroundColor: "#402b69" }}>
      <div className="flex items-center justify-between w-full">
        <div className="flex items-center gap-3">
          {/* Hamburger (Mobile Only) */}
          <button
            className="md:hidden text-white hover:bg-white/10 p-2 rounded-md"
            onClick={onMobileMenuClick}
          >
            <Menu size={24} />
          </button>

          {/* Logo */}
          <div
            className="flex items-center cursor-pointer"
            onClick={() => navigate("/")}
          >
            <img src="/logo.svg" alt="Glossa Logo" className="h-8 lg:h-9 w-auto" />
          </div>
        </div>

        <div className="flex items-center space-x-2 md:space-x-4 relative">
          {/* Verify (all pages when !claimed) or Quick (all pages except dashboard when claimed) */}
          {location.pathname !== "/" && (
            <div className="flex items-center">
              {!isSuperAdmin && !isClaimed ? (
                <Button
                  variant="ghost"
                  onClick={() => setVerifyModalOpen(true)}
                  className="text-white hover:bg-white/10 border border-amber-400/80 rounded-md h-10 px-3 flex items-center gap-2"
                  title="Verify Account"
                >
                  <ShieldCheck size={18} />
                  <span className="hidden sm:inline text-sm font-medium">Verify Account</span>
                </Button>
              ) : !isSuperAdmin && !location.pathname.includes("dashboard") ? (
                <QuickActionDropdown
                  onAction={handleQuickAction}
                  showPayment={quickPaymentType === "stylist"}
                />
              ) : null}
            </div>
          )}

          {/* Search Trigger Icon */}
          {!isSuperAdmin && (
            <div className="flex items-center">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setSearchOverlayOpen(true)}
                className="text-white hover:bg-white/10 p-2 border border-white/40 rounded-md w-10 h-10 flex items-center justify-center"
                title="Search"
              >
                <Search className="w-5 h-5" />
              </Button>
            </div>
          )}

          {/* Home Button */}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/")}
            className="hidden md:flex text-white hover:bg-white/10 p-2 border border-white/40 rounded-md w-10 h-10 items-center justify-center"
          >
            <img src="/bars/home.svg" alt="Home" className="w-5 h-5" />
          </Button>

          {/* Notification Button */}
          {!isSuperAdmin && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setNotificationSidebarOpen(true)}
              className="text-white hover:bg-white/10 p-2 border border-white/40 rounded-md w-10 h-10 flex items-center justify-center relative"
              title="Notifications"
            >
              <img src="/bars/ring.svg" alt="Notifications" className="w-5 h-5" />
              {notificationCount > 0 && (
                <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white border-2 border-[#402b69]">
                  {notificationCount}
                </span>
              )}
            </Button>
          )}

          {/* User Menu */}
          <div className="relative">
            <Button
              variant="ghost"
              onClick={() => setMenuOpen((prev) => !prev)}
              className="text-white hover:bg-white/10 border border-white/40 rounded-md w-10 md:w-auto px-2 md:px-3 h-10 flex items-center justify-center gap-1 md:gap-2"
            >
              <img src="/bars/profile.svg" alt="Profile" className="w-5 h-5" />
              <ChevronDown className="w-4 h-4" />
            </Button>

            {/* Dropdown */}
            {menuOpen && (
              <>
                {/* Backdrop */}
                <div
                  className="fixed inset-0 z-40"
                  onClick={() => setMenuOpen(false)}
                />

                {/* Enhanced Dropdown Menu */}
                <div className="absolute right-0 mt-2 w-72 bg-white rounded-xl shadow-2xl border border-gray-100 overflow-hidden z-50">
                  {user ? (
                    <>
                      {/* User Info Section */}
                      <div className="px-4 py-4 bg-white border-b border-gray-200">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center border-2 border-purple-200">
                            <span className="text-lg font-bold text-purple-600">
                              {user.name?.charAt(0).toUpperCase() || user.email?.charAt(0).toUpperCase() || 'U'}
                            </span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold text-sm truncate text-gray-900">
                              {user.name || 'User'}
                            </p>
                            <p className="text-xs text-gray-500 truncate">
                              {user.email || ''}
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Menu Items */}
                      <div className="py-2">
                        <button
                          onClick={() => {
                            navigate(isSuperAdmin ? "/superadmin/settings" : "/settings");
                            setMenuOpen(false);
                          }}
                          className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-100 transition-colors flex items-center gap-3"
                        >
                          <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                          <span>Settings</span>
                        </button>
                      </div>

                      {/* Divider */}
                      <div className="border-t border-gray-200"></div>

                      {/* Sign Out Button */}
                      <div className="p-2">
                        <button
                          onClick={() => {
                            logout();
                            setMenuOpen(false);
                          }}
                          className="w-full text-left px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors flex items-center gap-3 font-medium"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                          </svg>
                          <span>Sign Out</span>
                        </button>
                      </div>
                    </>
                  ) : (
                    <div className="py-2">
                      <button
                        onClick={() => {
                          navigate("/login");
                          setMenuOpen(false);
                        }}
                        className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                      >
                        Login
                      </button>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      <NotificationSidebar
        isOpen={notificationSidebarOpen}
        onClose={() => setNotificationSidebarOpen(false)}
        onUpdate={fetchNotificationCount}
      />
      <SearchOverlay
        isOpen={searchOverlayOpen}
        onClose={() => setSearchOverlayOpen(false)}
      />

      {/* Global Sidebars triggered by Quick Action */}
      <Portal>
        <AppointmentSidebar
          isOpen={isAppointmentSidebarOpen}
          onClose={() => {
            setIsAppointmentSidebarOpen(false);
            setAppointmentSidebarMode("Services");
          }}
          staffList={staffList}
          initialServiceType={appointmentSidebarMode}
          mode="dashboard"
        />
      </Portal>

      <Portal>
        {showQuickPaymentPanel && (
          <QuickPaymentPanel
            onClose={() => setShowQuickPaymentPanel(false)}
            onProceed={() => {
              setShowQuickPaymentPanel(false);
              setSidebarMode("quick");
            }}
            quickPaymentType={quickPaymentType}
          />
        )}
      </Portal>

      <VerifyAccountModal
        isOpen={verifyModalOpen}
        onClose={() => setVerifyModalOpen(false)}
        companyEmail={user?.company?.company_email ?? ""}
        onVerified={() => syncSettings()}
      />

      <Portal>
        {sidebarMode && (
          <PosPaymentStep
            selectedService={{
              id: "service-1",
              name: "Hair Cut",
              duration: "60 mins",
              price: `${currencySymbol}35`,
            }}
            selectedCustomer="Karen Davies"
            appointmentTime="9.30–10.30 am"
            tip={0}
            setTip={() => { }}
            goToFinalPayment={() => setSidebarMode(null)}
            onClose={() => setSidebarMode(null)}
            mode={sidebarMode}
          />
        )}
      </Portal>
    </nav>
  );
};

export default Navbar;
